/**
 * Certificate Verification Page — Public, no auth required
 * Route: /verify/certificate/:certId
 * Displays certificate validity and assessment details.
 */
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, X, Shield, ExternalLink, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL    = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

const LEVEL_CONFIG = {
  analyst_reviewed:     { label:"Analyst Reviewed",     color:"#6366F1", bg:"#6366F108" },
  third_party_verified: { label:"Third-Party Verified", color:"#059669", bg:"#05966908" },
};

export default function CertificateVerify() {
  const { certId } = useParams();
  const [cert,    setCert]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/verification/certificate/${certId}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status === 404 ? "Certificate not found" : "Failed to load");
        return r.json();
      })
      .then(d => { setCert(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [certId]);

  if (loading) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-[#059669] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !cert) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="text-center max-w-sm p-8">
        <AlertTriangle size={32} className="mx-auto text-amber-400 mb-3" />
        <h2 className="text-lg font-bold text-[#0F172A] mb-2">Certificate Not Found</h2>
        <p className="text-sm text-slate-500 mb-4">{error}</p>
        <Link to="/" className="text-sm text-[#059669] hover:underline">← Return to DeCarbonIQ</Link>
      </div>
    </div>
  );

  const cfg = LEVEL_CONFIG[cert.level] || LEVEL_CONFIG.analyst_reviewed;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="certificate-verify-page">
      <nav className="bg-[#0F172A] h-14 flex items-center justify-between px-6">
        <img src={LOGO_URL} alt="DeCarbonIQ" className="h-7 w-auto" style={{filter:"brightness(0) invert(1)"}} />
        <p className="text-xs text-slate-500 hidden sm:block">Certificate Verification</p>
      </nav>

      <div className="px-4 py-12">
        {/* Status */}
        <div className={`rounded-xl border-2 p-8 text-center mb-8 ${cert.is_valid ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}
          data-testid="cert-status">
          {cert.is_valid
            ? <CheckCircle2 size={48} className="mx-auto text-[#059669] mb-3" />
            : <X size={48} className="mx-auto text-red-500 mb-3" />}
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-1">
            {cert.is_valid ? "Certificate Valid" : "Certificate Revoked"}
          </h1>
          <p className={`text-sm ${cert.is_valid ? "text-emerald-700" : "text-red-700"}`}>
            {cert.is_valid ? `This certificate is genuine and was issued by DeCarbonIQ.` : `This certificate has been revoked. Reason: ${cert.revoked_reason || "—"}`}
          </p>
        </div>

        {/* Certificate Detail */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-6">
          <div className="px-6 py-5 border-b border-slate-100" style={{backgroundColor: cfg.bg}}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{backgroundColor:`${cfg.color}20`}}>
                <Shield size={22} style={{color: cfg.color}} />
              </div>
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Verification Level</p>
                <p className="text-lg font-bold" style={{color: cfg.color}}>{cfg.label}</p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {[
              ["Certificate ID",  cert.certificate_id],
              ["Issued by",       cert.issued_by || "DeCarbonIQ"],
              ["Issue date",      cert.issued_at?.slice(0,10)],
              ["Valid until",     cert.valid_until?.slice(0,10) || "—"],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between">
                <span className="text-sm text-slate-500">{label}</span>
                <span className="text-sm font-semibold text-slate-900 font-mono">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Assessment */}
        {cert.assessment && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Assessment</p>
            {[
              ["Organization", cert.organization?.name],
              ["Sector",       cert.organization?.sector],
              ["Country",      cert.organization?.country],
              ["Assessment",   cert.assessment?.title],
              ["Year",         cert.assessment?.reporting_year ? `FY${cert.assessment.reporting_year}` : "—"],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
                <span className="text-xs text-slate-400">{label}</span>
                <span className="text-xs font-semibold text-[#0F172A]">{value || "—"}</span>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {cert.summary && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Analyst Summary</p>
            <p className="text-sm text-slate-700 leading-relaxed">{cert.summary}</p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center">
          <Link to="/" className="text-sm text-[#059669] hover:underline flex items-center gap-1 justify-center">
            <ExternalLink size={13} /> Learn more about DeCarbonIQ Climate Intelligence
          </Link>
          <p className="text-xs text-slate-400 mt-3">
            This certificate was issued by DeCarbonIQ and reflects the verification status at time of issuance.
          </p>
        </div>
      </div>
    </div>
  );
}
